import Grid from "@mui/material/Grid2";
import { intl } from "LocaleWrapper";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { FormGroup } from "@cofano/cck";
import GasoilConsumptionPriceSourceSelect from "view/calculationmethods/components/select/GasoilConsumptionPriceSourceSelect";
import FormField from "components/cck/FormField";
import { GasoilConsumptionPriceSource, ReferenceRateUnit, ReferenceRateDateType } from "__generated__/graphql";
import ReferenceRateDateTypeSelect from "view/calculationmethods/components/select/ReferenceRateDateTypeSelect";
import { Fragment, ReactNode } from "react";
import NumberInput from "components/cck/NumberInput";
import type { CalculationMethodFormByTypeProps } from "@schemas/calculationmethods/schema";

type FormValues = {
	agreements: {
		calculationMethod: {
			priceSource?: GasoilConsumptionPriceSource;
			price?: number;
			referenceRateDateType?: ReferenceRateDateType;
		}
	}[];
}

type FormField = {
	id: string;
	render: () => ReactNode;
}

const GasoilConsumptionCalculationMethodForm = ({ index, currency, hiddenFields }: CalculationMethodFormByTypeProps) => {
	const { control, formState: { errors } } = useFormContext<FormValues>();

	const priceSource = useWatch({ control, name: `agreements.${index}.calculationMethod.priceSource` });

	const GasoilConsumptionCalculationMethodFormFields: FormField[] = [
		{
			id: "calculationMethod.priceSource",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.priceSource`}>
						<FormGroup label={intl.formatMessage({ id: "GASOIL_CONSUMPTION_PRICE_SOURCE" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.priceSource`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<GasoilConsumptionPriceSourceSelect {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.price",
			render: () => priceSource === GasoilConsumptionPriceSource.Manual && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.price`}>
						<FormGroup label={intl.formatMessage({ id: "PRICE_PER_LITER" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.price`}
								control={control}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonBefore={currency.symbol} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.referenceRateDateType",
			render: () => priceSource === GasoilConsumptionPriceSource.Kbn && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.referenceRateDateType`}>
						<FormGroup
							label={intl.formatMessage({ id: "REFERENCE_RATE_DATE_TYPE" })}
							overflow="hidden"
							required>
							<Controller
								name={`agreements.${index}.calculationMethod.referenceRateDateType`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<ReferenceRateDateTypeSelect referenceRateUnit={ReferenceRateUnit.GasolinePrice} {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	return (
		<>
			{GasoilConsumptionCalculationMethodFormFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

export default GasoilConsumptionCalculationMethodForm;
